// FILE: src/components/studyplan/CommandHero.js
//
// Study Plan Command Center hero: CoachAvatar (brand anchor, left), cert
// title + current-phase chip, readiness dial, exam countdown + CTAs.

import React from 'react';
import styles from './StudyPlanViewPage.module.css';
import CoachAvatar from '../coach/CoachAvatar';
import { STATUS_LABEL } from '../../services/readinessService';

function ReadinessDial({ score }) {
  const r = 46;
  const circ = 2 * Math.PI * r; // ≈ 289
  const filled = Math.max(0, Math.min(100, score)) / 100 * circ;
  return (
    <svg width="110" height="110" viewBox="0 0 110 110" role="img" aria-label={`Readiness ${Math.round(score)} out of 100`}>
      <circle cx="55" cy="55" r={r} fill="none" stroke="rgba(255,255,255,.14)" strokeWidth="11" />
      <circle
        cx="55" cy="55" r={r} fill="none" stroke="#ffd75e" strokeWidth="11"
        strokeLinecap="round" strokeDasharray={`${filled} ${circ}`}
        transform="rotate(-90 55 55)"
      />
      <text x="55" y="53" textAnchor="middle" fill="#fff" fontSize="24" fontWeight="800">{Math.round(score)}</text>
      <text x="55" y="70" textAnchor="middle" fill="#8fa2d9" fontSize="10">/ 100</text>
    </svg>
  );
}

export default function CommandHero({
  certLabel, phaseLabel, readiness, daysUntil, examDateLong,
  onContinueToday, canContinue, onEditPlan, hasPlan,
}) {
  const score = readiness?.score ?? 0;
  const statusLabel = STATUS_LABEL[readiness?.status] || 'Not started';
  return (
    <section className={styles.hero} data-testid="study-plan-view-hero">
      <div className={styles.heroAvatar}>
        <CoachAvatar pose="study" heightPx={216} decorative />
      </div>
      <div className={styles.heroTitleBlock}>
        <p className={styles.heroEyebrow}>Your Certification Journey</p>
        <h1 id="spv-title" className={styles.heroTitle}>{certLabel} Study Plan</h1>
        <div className={styles.heroChips}>
          {phaseLabel ? <span className={styles.phaseChip}>Phase: {phaseLabel}</span> : null}
        </div>
      </div>
      <div className={styles.dial}>
        <ReadinessDial score={score} />
        <div className={styles.dialLbl}>Readiness</div>
        <div className={styles.dialStatus}>{statusLabel}</div>
      </div>
      <div className={styles.heroDate}>
        <div className={styles.heroDays}>{daysUntil != null ? Math.max(daysUntil, 0) : '—'}</div>
        <div className={styles.heroDateLbl}>Days Remaining</div>
        <div className={styles.heroDateSub}>{examDateLong ? `Exam: ${examDateLong}` : 'No exam date set'}</div>
        <div className={styles.heroCtas}>
          <button
            type="button"
            className={styles.btnBig}
            onClick={() => onContinueToday?.()}
            disabled={!canContinue}
            data-testid="study-plan-view-continue-today"
          >Continue Today&rsquo;s Plan &rarr;</button>
          <button
            type="button"
            className={styles.btnHeroGhost}
            onClick={() => onEditPlan?.()}
            data-testid="study-plan-view-edit"
          >{hasPlan ? 'Edit exam date / plan' : 'Set your exam date'}</button>
        </div>
      </div>
    </section>
  );
}
